import React, { FC, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card } from 'components/Card'
import { useTranslation } from 'react-i18next'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Alert } from 'components/Alert'
import { useFormFields } from 'hooks/useFormFields'
import { useLogin } from 'services/auth'
import { Flex } from 'components/Flex'
import { Logo } from 'features/shared/Logo'
import styles from './Login.module.scss'
import { OrganizationRepository } from 'repository/organization'
import { StorageProvider } from 'providers/storage'

export type LoginFormData = {
  email: string
  password: string
}

export const Login: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [login, { status }] = useLogin()
  const [form, onFieldChange] = useFormFields({
    email: '',
    password: '',
  })

  React.useEffect(() => {
    document.cookie.split(';').forEach((c) => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
    })

    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name)
      })
    })

    //window.localStorage.clear()
    StorageProvider.remove('token')
    StorageProvider.remove('email')
  }, [])

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (form.email === '' || form.password === '') return

    login(form, {
      onSuccess: async () => {
        var organization = await OrganizationRepository.fetch()
        if (organization.subscription.level === 'Freemium') {
          navigate('/explore')
        } else {
          navigate('/dashboard')
        }
      },
    })
  }

  return (
    <Flex justify="center" align="center" className={styles.login}>
      <Card className={styles.card}>
        <Stack space="large">
          <Stack space="small">
            <Heading size="secondary">{t('login.title')}</Heading>
            <Text>{t('login.subtitle')}</Text>
          </Stack>

          <form onSubmit={onFormSubmit}>
            <Stack space="medium">
              <TextInput
                name="email"
                type="email"
                value={form.email}
                onChange={onFieldChange}
                label={t('login.form.email')}
                required
              />
              <TextInput
                name="password"
                type="password"
                value={form.password}
                onChange={onFieldChange}
                label={t('login.form.password')}
                required
              />

              <Button type="submit" loading={status === 'loading'}>
                {t('login.form.cta')}
              </Button>

              {status === 'error' && (
                <Alert tone="critical" full>
                  {t('login.error')}
                </Alert>
              )}
            </Stack>
          </form>

          <Text>
            {t('login.forgot_password')}{' '}
            <Link to="/forgot-password">{t('login.forgot_password_cta')}</Link>
          </Text>

          <Logo />
        </Stack>
      </Card>
    </Flex>
  )
}

Login.displayName = 'Login'
